<template>
  <div class="mcontainer">
    <!-- Header -->
    <div class="header-bar">
      <h2>BO Approval</h2>
      <div class="actions">
        <div class="search">
          <Icon icon="mdi:magnify" width="18" height="18" class="search-icon" />
          <input
            class="search-input"
            v-model="search"
            @input="debouncedFetch"
            placeholder="Search..."
          />
        </div>
        <select class="form-control" style="margin-bottom: 0;" v-model="status" @change="applyStatus">
          <option value="">All</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>
    </div>

    <!-- Card -->
    <div class="card">
      <table class="table">
        <thead>
          <tr>
            <th style="width:120px">Gambar</th>
            <th>Nama</th>
            <th>Pengusul</th>
            <th>Status</th>
            <th style="width:220px">Action</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="it in list" :key="it.id">
            <td>
              <img v-if="it.image" :src="it.image" class="thumb" alt="">
              <div v-else class="thumb placeholder">
                <Icon icon="mdi:image-off-outline" width="22" height="22" />
              </div>
            </td>
            <td>
              <div class="name">{{ it.name }}</div>
              <div class="desc muted">{{ truncate(it.description, 120) }}</div>
            </td>
            <td>
              {{ (it.user && it.user.fullname) || '-' }}
              <span class="muted">({{ (it.user && it.user.type) || '-' }})</span>
            </td>
            <td>
              <span :class="['badge', it.status]">{{ it.status }}</span>
            </td>
            <td>
              <div class="row-actions">
                <button
                  class="btn btn-info"
                  @click="openDetail(it)"
                  :disabled="loading"
                  title="Lihat Detail"
                >
                  <Icon icon="mdi:eye" width="16" height="16" />
                  Detail
                </button>
                <button
                  class="btn btn-success"
                  @click="approve(it.id)"
                  :disabled="loading || it.status === 'approved'"
                >
                  Approve
                </button>
                <button
                  class="btn btn-warning"
                  @click="reject(it.id)"
                  :disabled="loading || it.status === 'rejected'"
                >
                  Reject
                </button>
                <button
                  class="btn btn-danger"
                  @click="destroy(it.id)"
                  :disabled="loading"
                >
                  Delete
                </button>
              </div>
            </td>
          </tr>

          <tr v-if="!loading && list.length === 0">
            <td colspan="5" class="empty">Tidak ada data</td>
          </tr>
        </tbody>
      </table>

      <div class="loading-wrap" v-if="loading"><b-spinner small /></div>

      <!-- Pagination -->
      <div v-if="totalPage > 1" class="pager-wrap">
        <div class="pager-info">Page {{ page }} of {{ totalPage }}</div>
        <div class="pager-controls">
          <button class="icon-btn" :disabled="page===1 || loading" @click="go(1)" title="First">
            <Icon icon="mdi:chevron-double-left" width="18" height="18" />
          </button>
          <button class="icon-btn" :disabled="page===1 || loading" @click="go(page-1)" title="Prev">
            <Icon icon="mdi:chevron-left" width="18" height="18" />
          </button>

          <button
            v-for="p in pages"
            :key="p"
            class="num-btn"
            :class="{ active: p === page }"
            :disabled="p === page || loading"
            @click="go(p)"
          >
            {{ p }}
          </button>

          <button class="icon-btn" :disabled="page===totalPage || loading" @click="go(page+1)" title="Next">
            <Icon icon="mdi:chevron-right" width="18" height="18" />
          </button>
          <button class="icon-btn" :disabled="page===totalPage || loading" @click="go(totalPage)" title="Last">
            <Icon icon="mdi:chevron-double-right" width="18" height="18" />
          </button>
        </div>
      </div>
      <!-- End Pagination -->
    </div>

    <!-- Modal Detail -->
    <div v-if="detailOpen" class="modal-overlay" @click.self="closeDetail">
      <div class="modal-panel modal-lg">
        <div class="modal-header">
          <h3>Detail Business Opportunity</h3>
          <button class="icon-btn sm" @click="closeDetail">
            <Icon icon="mdi:close" width="18" height="18" />
          </button>
        </div>
        <div class="modal-body detail-body" v-if="selected">
          <div class="detail-media">
            <img v-if="getImage(selected)" :src="getImage(selected)" class="detail-img" alt="" />
            <div v-else class="detail-img placeholder">
              <Icon icon="mdi:image-off-outline" width="28" height="28" />
            </div>
          </div>
          <div class="detail-content">
            <h3 class="detail-title">{{ selected.name }}</h3>
            <div class="detail-meta">
              <span>Pengusul: <b>{{ selected.user ? selected.user.fullname : '-' }}</b></span>
              <span class="muted">({{ selected.user ? selected.user.type : '-' }})</span>
              <span :class="['badge', selected.status]">{{ selected.status }}</span>
            </div>
            <p class="detail-desc">{{ selected.description }}</p>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-light" @click="closeDetail">Tutup</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { Icon } from '@iconify/vue2'

export default {
  name: 'BusinessOpportunityAdmin',
  components: { Icon },
  data(){
    return {
      list: [],
      loading: false,
      page: 1,
      totalPage: 1,
      perPage: 10,
      status: 'pending',
      search: '',
      debouncedFetch: null,
      detailOpen: false,
      selected: null
    }
  },
  created(){
    this.debouncedFetch = this.debounce(this.fetchList, 500)
    this.fetchList()
  },
  computed:{
    pages(){
      const t=this.totalPage, cur=this.page, arr=[], win=5
      let s=Math.max(1, cur-2), e=Math.min(t, s+win-1)
      if (e-s<win-1) s=Math.max(1, e-win+1)
      for(let i=s;i<=e;i++) arr.push(i)
      return arr
    }
  },
  methods:{
    getImage(item){ if(!item) return null; return item.image_url || item.image || null },
    truncate(s,n){ return s && s.length>n ? s.slice(0,n)+'...' : (s||'') },
    debounce(fn,d){ let t=null; return (...a)=>{clearTimeout(t); t=setTimeout(()=>fn.apply(this,a), d)}},
    openDetail(it){ this.selected=it; this.detailOpen=true },
    closeDetail(){ this.detailOpen=false; this.selected=null },
    async fetchList(){
      this.loading=true
      try{
        const params = new URLSearchParams()
        params.set('page',this.page)
        params.set('per_page',this.perPage)
        if (this.status) params.set('status', this.status)
        if (this.search) params.set('search', this.search)
        const { data } = await axios.get(`/admin/bo?${params.toString()}`)
        this.list = (data && data.data) ? data.data : []
        this.page = (data && data.current_page) ? data.current_page : 1
        this.totalPage = (data && data.last_page) ? data.last_page : 1
      } finally {
        this.loading=false
      }
    },
    go(p){ if(p<1||p>this.totalPage||p===this.page) return; this.page=p; this.fetchList(); },
    applyStatus(){ this.page=1; this.fetchList(); },
    async approve(id){
      await axios.post(`/admin/bo/${id}/approve`)
      this.fetchList()
    },
    async reject(id){
      await axios.post(`/admin/bo/${id}/reject`)
      this.fetchList()
    },
    async destroy(id){
      if (!confirm('Yakin hapus?')) return
      await axios.delete(`/admin/bo/${id}`)
      this.fetchList()
    }
  }
}
</script>

<style scoped>
.header-bar{display:flex;align-items:center;justify-content:space-between;margin-bottom:12px}
.actions{display:flex;gap:10px;align-items:center}
.search{position:relative;min-width:260px;width:320px}
.search-icon{position:absolute;top:10px;left:12px;color:#9ca3af}
.search-input{width:100%;height:38px;border:1px solid #e5e7eb;border-radius:10px;padding:0 12px 0 36px;background:#fff}
.form-control{height:38px;border:1px solid #e5e7eb;border-radius:10px;padding:0 10px;background:#fff}
.card{background:#fff;border:1px solid #eef2f7;border-radius:14px;padding:16px}
.table{width:100%;border-collapse:separate;border-spacing:0}
.table thead th{background:#fafafa;border-bottom:1px solid #eef2f7;text-align:left;padding:10px;font-weight:600;color:#6b7280}
.table tbody td{border-bottom:1px solid #f3f4f6;padding:10px;vertical-align:top}
.thumb{width:100px;height:70px;object-fit:cover;border-radius:8px;border:1px solid #eee}
.thumb.placeholder{display:flex;align-items:center;justify-content:center;background:#fafafa;color:#9ca3af}
.badge{display:inline-block;padding:2px 8px;border-radius:9999px;font-size:12px;text-transform:capitalize}
.badge.pending{background:#fff7ed;color:#9a3412;border:1px solid #fed7aa}
.badge.approved{background:#ecfdf5;color:#065f46;border:1px solid #a7f3d0}
.badge.rejected{background:#fef2f2;color:#991b1b;border:1px solid #fecaca}
.row-actions{display:flex;gap:8px;flex-wrap:wrap}
.btn{height:32px;padding:0 10px;border-radius:8px;border:1px solid #e5e7eb;background:#fff;display:inline-flex;align-items:center;gap:4px}
.btn-info{background:#3b82f6;border-color:#3b82f6;color:#fff}
.btn-success{background:#10b981;border-color:#10b981;color:#fff}
.btn-warning{background:#f59e0b;border-color:#f59e0b;color:#fff}
.btn-danger{background:#ef4444;border-color:#ef4444;color:#fff}
.icon-btn{height:36px;width:36px;border:1px solid #e5e7eb;background:#fff;border-radius:10px}
.num-btn{min-width:36px;height:36px;border:1px solid #e5e7eb;background:#fff;border-radius:10px}
.num-btn.active{background:#2563eb;border-color:#2563eb;color:#fff}
.pager-wrap{display:flex;align-items:center;justify-content:space-between;margin-top:12px}
.pager-info{font-size:13px;color:#6b7280}
.empty{text-align:center;color:#9ca3af}

/* Modal Detail */
.modal-overlay{position:fixed;inset:0;background:rgba(15,23,42,.25);display:flex;align-items:center;justify-content:center;padding:24px 16px;z-index:999;overflow-y:auto}
.modal-panel{width:100%;max-width:560px;max-height:calc(100vh - 48px);background:#fff;border:1px solid #eef2f7;border-radius:14px;overflow:hidden;box-shadow:0 16px 40px rgba(2,6,23,.15);display:flex;flex-direction:column}
.modal-lg{max-width:900px}
.modal-header{display:flex;justify-content:space-between;align-items:center;padding:12px 16px;border-bottom:1px solid #eef2f7;flex-shrink:0}
.modal-body{padding:12px 16px;overflow-y:auto;flex:1;min-height:0}
.modal-footer{display:flex;justify-content:flex-end;gap:10px;padding:12px 16px;border-top:1px solid #eef2f7;flex-shrink:0}
.icon-btn{height:36px;width:36px;border:1px solid #e5e7eb;background:#fff;border-radius:10px;cursor:pointer}
.icon-btn.sm{height:32px;width:32px}
.btn-light{background:#f3f4f6;border-color:#e5e7eb;color:#374151}
.detail-body{display:grid;grid-template-columns:360px 1fr;gap:16px}
.detail-img{width:100%;height:260px;object-fit:cover;border-radius:12px;border:1px solid #eef2f7;background:#fafafa}
.detail-img.placeholder{display:flex;align-items:center;justify-content:center;color:#9ca3af}
.detail-title{margin:0 0 8px 0}
.detail-meta{display:flex;gap:10px;align-items:center;margin-bottom:8px;color:#6b7280;flex-wrap:wrap}
.detail-desc{white-space:pre-line;margin:0}
@media (max-width:768px){.detail-body{grid-template-columns:1fr}}
</style>